    import React, { useState, useEffect, useCallback } from 'react';
    import { useNavigate } from 'react-router-dom';
    import {
        Box, Typography, Paper, TextField, Button, CircularProgress,
        Alert, Container, Card, CardContent, Divider, IconButton,
        InputAdornment, Tooltip as MuiTooltip, Switch, FormControlLabel,
        List, ListItem, ListItemText, Chip, Grid, Link as MuiLink
    } from '@mui/material';
    import FileCopyIcon from '@mui/icons-material/FileCopy';
    import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
    import {
        ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Legend, Label, ReferenceArea, Tooltip as RechartsTooltip
    } from 'recharts';
    import { WordCloud } from '@isoterik/react-word-cloud';
    import api from './api';
    import config from './config';
    
    const getAcsStyleName = (proactivity, reactivity) => {
        if (proactivity > 0 && reactivity > 0) return "Expressive";
        if (proactivity < 0 && reactivity < 0) return "Analytical";
        if (proactivity > 0 && reactivity < 0) return "Driver";
        if (proactivity < 0 && reactivity > 0) return "Amiable";
        if (proactivity === 0 && reactivity > 0) return "Amiable Tendency";
        if (proactivity === 0 && reactivity < 0) return "Analytical Tendency";
        if (reactivity === 0 && proactivity > 0) return "Driver Tendency";
        if (reactivity === 0 && proactivity < 0) return "Analytical Tendency";
        if (proactivity === 0 && reactivity === 0) return "Balanced/Neutral";
        return "Not Determined";
    };
    
    const bcsChartColors = {
        individual: '#253977', 
        averagePRS: '#d47b27',   
        userSPI: '#755A57' 
    };
    
    const adjectiveCloudColors = {
        prsOnly: '#253977',
        spiOnly: '#755A57', 
        both: '#d47b27'
    };
    
    const CustomScatterShape = (props) => {
        const { cx, cy, fill, payload } = props;
        if (cx == null || cy == null) return null;
        const r = 8;
        const pointType = payload && payload.type ? payload.type : props.type;
    
        switch (pointType) {
            case 'averagePRS': 
                return <path d={`M ${cx} ${cy - r} L ${cx - r * 0.866} ${cy + r * 0.5} L ${cx + r * 0.866} ${cy + r * 0.5} Z`} fill={fill} stroke="#666" strokeWidth={1}/>;
            case 'userSPI': 
                return <circle cx={cx} cy={cy} r={r * 1.5} fill={fill} stroke="#333" strokeWidth={1.5}/>; 
            default: 
                return <circle cx={cx} cy={cy} r={r*0.75} fill={fill} stroke="#666" strokeWidth={1}/>;
        }
    };
    
    const CustomBcsTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const dataPointName = payload[0]?.payload?.name;
            let proactivityValue = null;
            let reactivityValue = null;
    
            payload.forEach(item => {
                if (item.name === 'Proactivity') {
                    proactivityValue = item.value;
                }
                if (item.name === 'Reactivity') {
                    reactivityValue = item.value;
                }
            });
    
            return (
                <Paper elevation={3} sx={{ p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '4px', border: '1px solid #ccc' }}>
                    {dataPointName && (
                        <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5, color: '#333' }}>
                            {dataPointName}
                        </Typography>
                    )}
                    {proactivityValue !== null && (
                        <Typography sx={{ fontSize: '0.875rem', p: '2px 0' }}>
                            {`Proactivity: ${proactivityValue.toFixed(1)}`}
                        </Typography>
                    )}
                    {reactivityValue !== null && (
                        <Typography sx={{ fontSize: '0.875rem', p: '2px 0' }}>
                            {`Reactivity: ${reactivityValue.toFixed(1)}`}
                        </Typography>
                    )}
                </Paper>
            );
        }
        return null;
    };
    
    
    function ProfessionalReputationUserPage({ onConfigChange, currentUserId, currentUserCohortId }) { // Accept props
        const navigate = useNavigate();
        const [pin, setPin] = useState('');
        const [confirmPin, setConfirmPin] = useState('');
        const [actualPinForDisplay, setActualPinForDisplay] = useState(''); 
        const [retrievedPlaintextPin, setRetrievedPlaintextPin] = useState(''); 
        const [uniqueSurveyToken, setUniqueSurveyToken] = useState('');
        const [isAcceptingResponses, setIsAcceptingResponses] = useState(true);
        const [pinIsSet, setPinIsSet] = useState(false);
    
        const [loadingConfig, setLoadingConfig] = useState(true);
        const [actionLoading, setActionLoading] = useState(false);
        const [error, setError] = useState('');
        const [successMessage, setSuccessMessage] = useState('');
    
        const [prsResults, setPrsResults] = useState(null);
        const [loadingPrsResults, setLoadingPrsResults] = useState(true);
        const [prsResultsError, setPrsResultsError] = useState('');
    
        const [spiAcs, setSpiAcs] = useState(null); 
        const [spiAdjectives, setSpiAdjectives] = useState([]); 
        const [loadingSpiData, setLoadingSpiData] = useState(true);
    
        const shareableLink = uniqueSurveyToken ? `${window.location.origin}/survey/prs/${uniqueSurveyToken}` : '';
    
        const fetchPrsConfig = useCallback(async () => {
            // This API call uses the token of the (potentially impersonated) user.
            // No need to pass currentUserId explicitly as the backend infers it from the token.
            setLoadingConfig(true); setError('');
            try {
                const response = await api.get('/api/prs/config');
                setUniqueSurveyToken(response.data.unique_survey_token || '');
                setPinIsSet(!!response.data.pin_set); 
                setIsAcceptingResponses(!!response.data.is_accepting_responses);
                setRetrievedPlaintextPin(response.data.pin_plaintext || ''); 
                if (onConfigChange) {
                    onConfigChange();
                }
            } catch (err) {
                setError(err.response?.data?.message || "Failed to load PRS configuration. Please try again.");
                console.error("Error fetching PRS config:", err);
            } finally {
                setLoadingConfig(false);
            }
        }, [onConfigChange]); 
    
        useEffect(() => {
            fetchPrsConfig();
        }, [fetchPrsConfig]);
    
        useEffect(() => {
            const fetchCurrentUserSpiData = async () => {
                // Use props directly
                if (currentUserId && currentUserCohortId) {
                    console.log("PRS Page: Fetching SPI Data with UserID:", currentUserId, "CohortID:", currentUserCohortId);
                    setLoadingSpiData(true);
                    try {
                       const spiResponse = await api.get(`/api/spi/results/${currentUserId}/${currentUserCohortId}`);
                       if (spiResponse.data && spiResponse.data.summary_calculations?.attitudinal_communication_style) {
                           const rawAcs = spiResponse.data.summary_calculations.attitudinal_communication_style;
                           setSpiAcs({
                               proactivity: (rawAcs.proactivity_score || 0) / 2,
                               reactivity: (rawAcs.reactivity_score || 0) / 2
                           });
                       } else {
                           setSpiAcs(null);
                       }
                       if (spiResponse.data && spiResponse.data.sections?.Adjectives?.items?.[0]?.selected_adjectives) {
                           setSpiAdjectives(spiResponse.data.sections.Adjectives.items[0].selected_adjectives);
                       } else {
                            setSpiAdjectives([]);
                       }
                    } catch (spiErr) {
                       console.error("Failed to fetch SPI data for PRS page:", spiErr);
                       setSpiAcs(null);
                       setSpiAdjectives([]);
                    } finally {
                        setLoadingSpiData(false);
                    }
                } else {
                   console.warn("PRS Page: User ID or Cohort ID missing from props, cannot fetch SPI data.", {currentUserId, currentUserCohortId});
                   setSpiAcs(null);
                   setSpiAdjectives([]);
                   setLoadingSpiData(false);
                }
            };
            fetchCurrentUserSpiData();
        }, [currentUserId, currentUserCohortId]); // Depend on props
    
        const fetchPrsResults = useCallback(async () => {
            // This API call uses the token of the (potentially impersonated) user.
            // No need to pass currentUserId explicitly.
            if (pinIsSet && uniqueSurveyToken) { 
                setLoadingPrsResults(true); setPrsResultsError('');
                try {
                    const response = await api.get('/api/prs/results');
                    setPrsResults(response.data);
                } catch (err) {
                    setPrsResultsError(err.response?.data?.message || "Failed to load PRS results.");
                    console.error("Error fetching PRS results:", err);
                    setPrsResults(null);
                } finally {
                    setLoadingPrsResults(false);
                }
            } else {
                setPrsResults(null); 
                setLoadingPrsResults(false);
            }
        }, [pinIsSet, uniqueSurveyToken]); 
    
        useEffect(() => {
            fetchPrsResults();
        }, [fetchPrsResults]); 
    
        const handleSetPin = async (e) => {
            e.preventDefault();
            if (pin !== confirmPin) { setError("PINs do not match."); return; }
            if (pin.length < 4) { setError("PIN must be at least 4 characters long."); return; }
            setActionLoading(true); setError(''); setSuccessMessage('');
            try {
                const response = await api.post('/api/prs/config', { pin }); 
                setPinIsSet(true);
                setUniqueSurveyToken(response.data.unique_survey_token);
                setIsAcceptingResponses(true); 
                setActualPinForDisplay(pin); 
                setRetrievedPlaintextPin(pin); 
                setSuccessMessage(response.data.message || "PIN set successfully! Your survey is now active.");
                setPin(''); setConfirmPin('');
                if (onConfigChange) onConfigChange();
            } catch (err) {
                setError(err.response?.data?.message || "Failed to set PIN.");
            } finally {
                setActionLoading(false);
            }
        };
    
        const handleToggleAcceptingResponses = async () => {
            setActionLoading(true); setError(''); setSuccessMessage('');
            try {
                const response = await api.put('/api/prs/config', { is_accepting_responses: !isAcceptingResponses });
                setIsAcceptingResponses(response.data.is_accepting_responses);
                setSuccessMessage(response.data.message || "Survey status updated.");
                if (onConfigChange) onConfigChange();
            } catch (err) {
                setError(err.response?.data?.message || "Failed to update survey status.");
            } finally {
                setActionLoading(false);
            }
        };
    
        const handleCopyToClipboard = () => {
            let pinTextForInvitation = "[SET YOUR PIN FIRST]";
            if (actualPinForDisplay) { 
                pinTextForInvitation = actualPinForDisplay;
            } else if (retrievedPlaintextPin) { 
                pinTextForInvitation = retrievedPlaintextPin;
            } else if (pinIsSet) { 
                pinTextForInvitation = "[YOUR CURRENTLY SET SURVEY PIN - Please insert it here]";
            }
    
            const textToCopy = `I'm gathering feedback on my professional reputation and would greatly appreciate your anonymous insights. Please take a few minutes to complete this survey by visiting the following link and using the PIN provided:\n\nSurvey Link: ${shareableLink}\nYour PIN: ${pinTextForInvitation}\n\nThank you for your time and valuable input!`;
            navigator.clipboard.writeText(textToCopy)
                .then(() => setSuccessMessage("Invitation text copied to clipboard! Please verify the PIN before sending."))
                .catch(err => setError("Failed to copy text."));
        };
    
        const handleDownloadCsv = async () => {
            setActionLoading(true); setError(''); setSuccessMessage('');
            try {
                const response = await api.get('/api/prs/results/download', { responseType: 'blob' });
                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none'; a.href = url;
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                const userNameForFile = prsResults?.user_name?.replace(/\s+/g, '_') || 'user';
                a.download = `prs_responses_${userNameForFile}_${timestamp}.csv`;
                document.body.appendChild(a); a.click();
                window.URL.revokeObjectURL(url); a.remove();
                setSuccessMessage("PRS data download initiated.");
            } catch (err) {
                setError(err.response?.data?.message || "Failed to download PRS data. This may be a server-side issue.");
                console.error("Error downloading CSV:", err.response || err);
            } finally {
                setActionLoading(false);
            }
        };
    
        const renderBcsBubbleChart = () => {
            const individualPrsResponses = prsResults?.behavioral_communication_style_responses || [];
            const hasPrsAverage = prsResults?.behavioral_communication_style;
            const hasSpiAcsData = spiAcs && typeof spiAcs.proactivity === 'number' && typeof spiAcs.reactivity === 'number';
    
            if (individualPrsResponses.length === 0 && !hasPrsAverage && !hasSpiAcsData) {
                 return <Typography sx={{textAlign:'center', py:2, color: 'text.secondary'}}>Data for Behavioral Communication Styles chart is not yet available.</Typography>;
            }
    
            const chartData = [];
            if (individualPrsResponses.length > 0) {
                const groupedResponses = individualPrsResponses.reduce((acc, resp) => {
                    const key = `${resp.proactivity_score},${resp.reactivity_score}`;
                    if (!acc[key]) {
                        acc[key] = { ...resp, count: 0, respondent_identifiers: [] };
                    }
                    acc[key].count += 1;
                    if(resp.respondent_identifier) {
                        acc[key].respondent_identifiers.push(resp.respondent_identifier);
                    }
                    return acc;
                }, {});
    
                Object.values(groupedResponses).forEach((resp, index) => {
                    chartData.push({
                        x: resp.proactivity_score,
                        y: resp.reactivity_score,
                        z: resp.count * 50,
                        name: resp.count > 1 ? `Grouped Response (${resp.count} respondents)` : (resp.respondent_identifiers[0] || `Individual Response ${index + 1}`),
                        type: 'individual',
                        count: resp.count 
                    });
                });
            }
            
            if (hasPrsAverage) {
                chartData.push({
                    x: prsResults.behavioral_communication_style.average_proactivity_score,
                    y: prsResults.behavioral_communication_style.average_reactivity_score,
                    z: 150, 
                    name: `Average PRS (${getAcsStyleName(
                        prsResults.behavioral_communication_style.average_proactivity_score,
                        prsResults.behavioral_communication_style.average_reactivity_score
                    )})`,
                    type: 'averagePRS',
                });
            }
    
            if (hasSpiAcsData) {
                chartData.push({
                    x: spiAcs.proactivity,
                    y: spiAcs.reactivity,
                    z: 150, 
                    name: `Your SPI ACS (${getAcsStyleName(spiAcs.proactivity, spiAcs.reactivity)})`,
                    type: 'userSPI',
                });
            }
            
            const axisTicks = Array.from({ length: 19 }, (_, i) => i - 9);
    
            return (
                <ResponsiveContainer width="90%" aspect={1}>
                    <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" dataKey="x" name="Proactivity" domain={[-9, 9]} ticks={axisTicks} allowDataOverflow={true}>
                            <Label value="Proactivity" offset={-25} position="insideBottom" style={{ fill: '#555', fontSize: '0.9rem' }}/>
                        </XAxis>
                        <YAxis type="number" dataKey="y" name="Reactivity" domain={[-9, 9]} ticks={axisTicks} allowDataOverflow={true}>
                            <Label value="Reactivity" angle={-90} position="insideLeft" style={{ textAnchor: 'middle', fill: '#555', fontSize: '0.9rem' }} />
                        </YAxis>
                        <ZAxis type="number" dataKey="z" range={[30, 200]} name="Frequency" /> 
                        
                        <ReferenceArea x1={0.05} x2={9} y1={0.05} y2={9} stroke="none" fill="#e0f7fa" fillOpacity={0.3} label={{ value: "Expressive", position: "insideTopRight", fill:"#00796b", fontSize: "1rem" }}/>
                        <ReferenceArea x1={-9} x2={-0.05} y1={-9} y2={-0.05} stroke="none" fill="#fff3e0" fillOpacity={0.3} label={{ value: "Analytical", position: "insideBottomLeft", fill:"#ef6c00", fontSize: "1rem"}}/>
                        <ReferenceArea x1={0.05} x2={9} y1={-9} y2={-0.05} stroke="none" fill="#e8f5e9" fillOpacity={0.3} label={{ value: "Driver", position: "insideBottomRight", fill:"#2e7d32", fontSize: "1rem"}}/>
                        <ReferenceArea x1={-9} x2={-0.05} y1={0.05} y2={9} stroke="none" fill="#fce4ec" fillOpacity={0.3} label={{ value: "Amiable", position: "insideTopLeft", fill:"#d81b60", fontSize: "1rem"}}/>
                        <ReferenceArea x1={-0.05} x2={0.05} fill="gray" fillOpacity={0.2} yAxisId={0} />
                        <ReferenceArea y1={-0.05} y2={0.05} fill="gray" fillOpacity={0.2} xAxisId={0} />
    
                        <RechartsTooltip 
                            cursor={{ strokeDasharray: '3 3' }} 
                            content={<CustomBcsTooltip />} 
                        />
                        <Legend verticalAlign="top" height={36} 
                            payload={[
                                { value: 'Individual PRS Responses', type: 'circle', id: 'ID01', color: bcsChartColors.individual },
                                { value: 'Average PRS BCS', type: 'triangle', id: 'ID02', color: bcsChartColors.averagePRS },
                                ...(hasSpiAcsData ? [{ value: 'Your SPI ACS', type: 'circle', id: 'ID03', color: bcsChartColors.userSPI }] : []),
                            ]}
                        />
                        <Scatter name="Individual PRS Responses" data={chartData.filter(d => d.type === 'individual')} shape={(props) => <CustomScatterShape {...props} type="individual" />} fill={bcsChartColors.individual} />
                        <Scatter name="Average PRS BCS" data={chartData.filter(d => d.type === 'averagePRS')} shape={(props) => <CustomScatterShape {...props} type="averagePRS" />} fill={bcsChartColors.averagePRS} />
                        {hasSpiAcsData && <Scatter name="Your SPI ACS" data={chartData.filter(d => d.type === 'userSPI')} shape={(props) => <CustomScatterShape {...props} type="userSPI" />} fill={bcsChartColors.userSPI} />}
                    </ScatterChart>
                </ResponsiveContainer>
            );
        };
    
        const renderAdjectivesWordCloud = () => {
            const hasPrsAdjectives = prsResults?.adjectives && Object.keys(prsResults.adjectives).length > 0;
            const hasSpiAdjectivesData = spiAdjectives && spiAdjectives.length > 0;
    
            if (!hasPrsAdjectives && !hasSpiAdjectivesData) {
                 return <Typography sx={{textAlign:'center', py:2, color: 'text.secondary'}}>Adjective data is not yet available.</Typography>;
            }
    
            const prsAdjArray = hasPrsAdjectives ? Object.entries(prsResults.adjectives).map(([text, value]) => ({ text, value })) : [];
            const currentSpiAdjectives = spiAdjectives || []; 
            const spiAdjSet = new Set(currentSpiAdjectives);
            const prsAdjTextSet = new Set(prsAdjArray.map(a => a.text));
    
            const wordCloudData = []; 
    
            prsAdjArray.forEach(adj => {
                if (spiAdjSet.has(adj.text)) { 
                    wordCloudData.push({ text: adj.text, value: adj.value, color: adjectiveCloudColors.both });
                } else { 
                    wordCloudData.push({ text: adj.text, value: adj.value, color: adjectiveCloudColors.prsOnly });
                }
            });
    
            if (hasSpiAdjectivesData) {
                spiAdjSet.forEach(adjText => {
                    if (!prsAdjTextSet.has(adjText)) { 
                        const prsTotalValue = prsAdjArray.reduce((acc,curr) => acc + curr.value, 0);
                        const prsLength = prsAdjArray.length || 1;
                        const spiOnlyValue = prsAdjArray.length > 0 ? Math.max(1, Math.floor(prsTotalValue / prsLength / 2)) : 5; 
                        wordCloudData.push({ 
                            text: adjText, 
                            value: spiOnlyValue, 
                            color: adjectiveCloudColors.spiOnly 
                        });
                    }
                });
            }
            
            if (wordCloudData.length === 0) {
                return <Typography sx={{textAlign:'center', py:2, color: 'text.secondary'}}>No adjectives to display in the word cloud.</Typography>;
            }
            
            const values = wordCloudData.map(w => w.value);
            const minWordValue = values.length > 0 ? Math.min(...values) : 1;
            const maxWordValue = values.length > 0 ? Math.max(...values) : 10;
    
            const scaleFontSize = (value, outMin = 12, outMax = 30) => { 
                if (minWordValue === maxWordValue || values.length === 1) return (outMin + outMax) / 2;
                return ((value - minWordValue) * (outMax - outMin)) / (maxWordValue - minWordValue) + outMin;
            };
    
            const legendItems = [
                { color: adjectiveCloudColors.prsOnly, label: 'PRS Only' },
                { color: adjectiveCloudColors.spiOnly, label: 'SPI Only (You)' },
                { color: adjectiveCloudColors.both, label: 'Both PRS & SPI' }
            ];
    
            return (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        {legendItems.map(item => (
                            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 14, height: 14, backgroundColor: item.color, mr: 0.5, borderRadius: '2px' }} />
                                <Typography variant="caption">{item.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px dashed lightgrey', p: 1, minHeight: '550px', mt: 3 }}> 
                        <WordCloud
                            words={wordCloudData}
                            width={500} 
                            height={500} 
                            fill={(word) => word.color}
                            padding={1} 
                            font="'Inter', sans-serif"
                            fontSize={(word) => scaleFontSize(word.value)}
                            rotate={() => (Math.random() > 0.7 ? (Math.random() > 0.5 ? 90 : -90) : 0)}
                            enableTooltip={true}
                        />
                    </Box>
                </>
            );
        };
    
        // Show loading spinner if any of the primary data fetches are in progress
        // OR if the necessary IDs for fetching are not yet available from props.
        if (loadingConfig || loadingSpiData || (pinIsSet && uniqueSurveyToken && loadingPrsResults) || !currentUserId || !currentUserCohortId) { 
            // Added check for currentUserId and currentUserCohortId to prevent premature rendering before props are ready
            console.log("PRS Page: Loading state active.", {loadingConfig, loadingSpiData, pinIsSet, uniqueSurveyToken, loadingPrsResults, currentUserId, currentUserCohortId});
            return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
        }
    
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 3 }}>
                        My Professional Reputation Survey
                    </Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
                    {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>{successMessage}</Alert>}
    
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent sx={{ textAlign: 'left' }}>
                                    <Typography variant="h6" gutterBottom>Survey Configuration</Typography>
                                    <Divider sx={{ my: 1 }} />
                                    {uniqueSurveyToken ? (
                                        <Box sx={{ mb: 2, mt: 1 }}>
                                            <Typography variant="body1" sx={{ mb: 1 }}>Your unique survey link:</Typography>
                                            <TextField
                                                fullWidth variant="outlined" value={shareableLink}
                                                InputProps={{
                                                    readOnly: true, 
                                                    style: { overflowX: 'auto'}, 
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <MuiTooltip title="Copy link">
                                                                <IconButton onClick={() => navigator.clipboard.writeText(shareableLink).then(() => setSuccessMessage("Link Copied!"))} edge="end">
                                                                    <FileCopyIcon />
                                                                </IconButton>
                                                            </MuiTooltip>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                size="small"
                                            />
                                        </Box>
                                    ) : <Typography variant="body2" color="textSecondary">Your unique link will appear here once your PIN is set.</Typography>}
    
                                    <form onSubmit={handleSetPin}>
                                        <Typography variant="body1" gutterBottom sx={{ mt: uniqueSurveyToken ? 2 : 0 }}>
                                            {pinIsSet ? "Update your PIN (min 4 characters):" : "Set a PIN for your survey (min 4 characters):"}
                                        </Typography>
                                        <TextField label="New PIN" type="password" value={pin} onChange={(e) => setPin(e.target.value)} variant="outlined" size="small" fullWidth sx={{ mb: 1 }} />
                                        <TextField label="Confirm New PIN" type="password" value={confirmPin} onChange={(e) => setConfirmPin(e.target.value)} variant="outlined" size="small" fullWidth sx={{ mb: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                            <Button type="submit" variant="contained" disabled={actionLoading} sx={{ width: 'auto' }}>
                                                {actionLoading && pin ? <CircularProgress size={24} /> : (pinIsSet ? "Update PIN" : "Set PIN & Activate Survey")}
                                            </Button>
                                        </Box>
                                    </form>
                                    {pinIsSet && uniqueSurveyToken && (
                                        <Box sx={{ mt: 3, border: '1px solid #eee', p: 2, borderRadius: 1 }}>
                                            <Typography variant="subtitle1" gutterBottom>Shareable Invitation Text:</Typography>
                                            <Box sx={{ backgroundColor: 'grey.100', p: 1, borderRadius: 1, mb: 1, overflowX: 'auto' }}>
                                                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                                                    {`I'm gathering feedback on my professional reputation and would greatly appreciate your anonymous insights. Please take a few minutes to complete this survey by visiting the following link and using the PIN provided:\n\nSurvey Link: ${shareableLink}\nYour PIN: ${actualPinForDisplay || retrievedPlaintextPin || (pinIsSet ? "[YOUR CURRENTLY SET SURVEY PIN - Please insert it here]" : "[SET YOUR PIN FIRST]")}\n\nThank you for your time and valuable input!`}
                                                </Typography>
                                            </Box>
                                            <Button onClick={handleCopyToClipboard} startIcon={<FileCopyIcon />} size="small" variant="outlined">
                                                Copy Invitation Text
                                            </Button>
                                        </Box>
                                    )}
                                    <Box sx={{ mt: 2 }}>
                                        <FormControlLabel
                                            control={<Switch checked={isAcceptingResponses} onChange={handleToggleAcceptingResponses} disabled={actionLoading || !pinIsSet} />}
                                            label={isAcceptingResponses ? "Survey is Active (Accepting Responses)" : "Survey is Inactive (Not Accepting Responses)"}
                                        />
                                        {!pinIsSet && <Typography variant="caption" color="textSecondary" display="block">Set a PIN to activate your survey and generate the shareable link.</Typography>}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
    
                        <Grid item xs={12}>
                            <Card sx={{ height: '100%' }}>
                                <CardContent sx={{ textAlign: 'left' }}>
                                    <Typography variant="h6" gutterBottom>Your PRS Results Summary</Typography>
                                    <Divider sx={{ my: 1 }} />
                                    {loadingPrsResults && pinIsSet && uniqueSurveyToken && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress /></Box>}
                                    {prsResultsError && <Alert severity="error" sx={{ textAlign: 'left' }}>{prsResultsError}</Alert>}
                                    {prsResults ? (
                                        prsResults.number_of_respondents > 0 ? (
                                            <Box>
                                                <Typography variant="body1" sx={{ mb: 1 }}>Summary of <strong>{prsResults.number_of_respondents}</strong> Response(s):</Typography>
                                                <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Avg. Behavioral Communication Style:</Typography>
                                                    <Chip label={prsResults.behavioral_communication_style?.style_name || 'N/A'} color="secondary" sx={{ mt: 0.5, mb: 1 }}/>
                                                    <Typography variant="body1">Composite Proactivity: {prsResults.behavioral_communication_style?.average_proactivity_score?.toFixed(1) ?? 'N/A'}</Typography>
                                                    <Typography variant="body1">Composite Reactivity: {prsResults.behavioral_communication_style?.average_reactivity_score?.toFixed(1) ?? 'N/A'}</Typography>
                                                </Box>
                                                <Box sx={{ my: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Top Adjectives Mentioned:</Typography>
                                                    {Object.entries(prsResults.adjectives || {}).length > 0 ?
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{Object.entries(prsResults.adjectives).map(([adj, count]) => <Chip key={adj} label={`${adj} (${count})`} size="small" variant="outlined" />)}</Box> :
                                                        <Typography variant="body2" color="text.secondary">No adjectives data yet.</Typography>
                                                    }
                                                </Box>
                                                <Box sx={{ my: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Communication Methods Used:</Typography>
                                                    {Object.entries(prsResults.communication_methods || {}).length > 0 ?
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>{Object.entries(prsResults.communication_methods).map(([method, count]) => <Chip key={method} label={`${method} (${count})`} size="small" variant="outlined" />)}</Box> :
                                                        <Typography variant="body2" color="text.secondary">No communication methods data yet.</Typography>
                                                    }
                                                </Box>
                                                 <Button 
                                                    onClick={handleDownloadCsv} 
                                                    variant="outlined" 
                                                    color="primary" 
                                                    sx={{mt:2, display:'flex', alignItems: 'center', width:'fit-content', mx:'auto'}} 
                                                    disabled={actionLoading || !prsResults || prsResults.number_of_respondents === 0} 
                                                    startIcon={<DownloadForOfflineIcon />} 
                                                >
                                                    {actionLoading && !prsResults ? <CircularProgress size={20} color="inherit"/> : "Download Raw Data (CSV)"}
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Typography sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>No responses have been received for your Professional Reputation Survey yet.</Typography>
                                        )
                                    ) : (
                                        !loadingPrsResults && !prsResultsError && <Typography sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>PRS not yet configured or no results available. Please set a PIN to activate.</Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
    
                        {pinIsSet && ( (prsResults?.behavioral_communication_style_responses && prsResults.behavioral_communication_style_responses.length > 0) || (prsResults?.behavioral_communication_style) || (spiAcs && Object.keys(spiAcs).length > 0)) && 
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent sx={{pb: 3}}>
                                        <Typography variant="h6" gutterBottom align="left">Behavioral Communication Styles Analysis</Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{mb:2, textAlign:'left'}}>
                                            This chart plots perceived Behavioral Communication Styles from PRS responses alongside your self-identified Attitudinal Communication Style (ACS) from the SPI.
                                            {!(prsResults?.behavioral_communication_style_responses && prsResults.behavioral_communication_style_responses.length > 0) && 
                                                " Individual PRS responses data is not yet available from the backend or no responses have been recorded."}
                                        </Typography>
                                        {renderBcsBubbleChart()}
                                    </CardContent>
                                </Card>
                            </Grid>
                        }
                        
                         {pinIsSet && ( (prsResults?.adjectives && Object.keys(prsResults.adjectives).length > 0) || (spiAdjectives && spiAdjectives.length > 0)) &&
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent sx={{pb: 3}}>
                                        <Typography variant="h6" gutterBottom align="left">Adjective Analysis: You vs. Your Reviewers</Typography>
                                        <Divider sx={{ my: 1, mb: 2 }} /> 
                                         <Typography variant="body2" color="text.secondary" sx={{mb:1, textAlign:'left'}}>
                                            This word cloud visualizes adjectives used by your PRS respondents and those you selected in your Self-Perception Inventory (SPI).
                                            {(!spiAdjectives || spiAdjectives.length === 0) && " Your SPI adjectives are not yet loaded or available."}
                                        </Typography>
                                        {renderAdjectivesWordCloud()}
                                    </CardContent>
                                </Card>
                            </Grid>
                        }
    
                        {prsResults && ( (prsResults.formative_feedback && prsResults.formative_feedback.length > 0) || (prsResults.reinforcing_feedback && prsResults.reinforcing_feedback.length > 0) ) &&
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent sx={{ textAlign: 'left' }}>
                                        <Typography variant="h6" gutterBottom align="center">Formative & Reinforcing Feedback</Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{mb:2, textAlign:'center'}}>
                                            Direct feedback from your respondents.
                                        </Typography>
                                        
                                        {prsResults.formative_feedback && prsResults.formative_feedback.length > 0 && (
                                            <Box sx={{my:2}}>
                                                <Typography variant="subtitle1" sx={{fontWeight:'bold'}}>Suggestions for Growth (Formative):</Typography>
                                                <List dense disablePadding>
                                                    {prsResults.formative_feedback.map((fb, i) => 
                                                        <ListItem key={`form-${i}`} sx={{pl:0, pt:0, pb:0.5}}>
                                                            <ListItemText primaryTypographyProps={{variant:'body2'}} primary={`"${fb}"`}/>
                                                        </ListItem>
                                                    )}
                                                </List>
                                            </Box>
                                        )}
    
                                        {prsResults.reinforcing_feedback && prsResults.reinforcing_feedback.length > 0 && (
                                            <Box sx={{my:2}}>
                                                <Typography variant="subtitle1" sx={{fontWeight:'bold'}}>Strengths to Continue (Reinforcing):</Typography>
                                                <List dense disablePadding>
                                                    {prsResults.reinforcing_feedback.map((fb, i) => 
                                                        <ListItem key={`rein-${i}`} sx={{pl:0, pt:0, pb:0.5}}>
                                                            <ListItemText primaryTypographyProps={{variant:'body2'}} primary={`"${fb}"`}/>
                                                        </ListItem>
                                                    )}
                                                </List>
                                            </Box>
                                        )}
                                         {(!prsResults.formative_feedback || prsResults.formative_feedback.length === 0) && 
                                          (!prsResults.reinforcing_feedback || prsResults.reinforcing_feedback.length === 0) && (
                                            <Typography variant="body2" color="text.secondary" sx={{textAlign:'center'}}>No formative or reinforcing feedback received yet.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        }
                    </Grid>
                </Paper>
            </Container>
        );
    }
    
    export default ProfessionalReputationUserPage;
    